import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useKeycloak } from '@react-keycloak/web';
import { createProjectPhase } from '../../api/projects';
import { toast } from 'react-toastify';
import { FaChevronDown, FaPlus, FaTimes, FaCheck } from 'react-icons/fa';
import { DesignSystem, cssClasses, getPrimaryButtonStyle } from '../../styles/designSystem';

// Helper function to format phase name for display
const formatPhaseNameForDisplay = (phaseName: string): string => {
  return phaseName === 'Phase1' ? 'Phase' : phaseName;
};

interface PhaseSelectBadgeProps {
  phases: ProjectPhaseInterface[];
  selectedPhaseId: string;
  projectId: string;
  onChange: (phaseId: string) => void;
  onCreate?: (phaseId: string) => void;
  onProjectUpdate?: () => Promise<void>;
}

export const PhaseSelectBadge = ({
  phases,
  selectedPhaseId,
  projectId,
  onChange,
  onCreate,
  onProjectUpdate
}: PhaseSelectBadgeProps) => {
  const { keycloak: { token } } = useKeycloak();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedPhase = phases.find(p => p.id === selectedPhaseId);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 256)
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking inside the dropdown or input field
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking the button
      if (buttonRef.current && buttonRef.current.contains(target)) {
        return;
      }
      
      // Close dropdown if clicking outside
        setIsOpen(false);
      setShowCreateForm(false);
    };

    const handleScroll = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    if (isOpen) {
      updateDropdownPosition();
    document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleShowCreateForm = () => {
    setShowCreateForm(true);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      updateDropdownPosition();
    }
  };

  // Create Phase Form Component
  const CreatePhaseForm = () => {
    const [localPhaseName, setLocalPhaseName] = useState('');
    const localInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (localInputRef.current) {
        localInputRef.current.focus();
      }
  }, []);

    const handleLocalKeyPress = (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        handleCreatePhaseWithName(localPhaseName);
      } else if (e.key === 'Escape') {
        handleCancelCreate();
      }
    };

    const handleCreatePhaseWithName = async (phaseName: string) => {
      if (!phaseName.trim() || !token) return;

    setIsCreating(true);
    try {
      const newPhase = await createProjectPhase(token, projectId, {
        id: crypto.randomUUID(),
        project_id: projectId,
          name: phaseName.trim(),
        status: 'NOT_STARTED',
      });

        // Update project data to refresh phases list
        if (onProjectUpdate) {
          await onProjectUpdate();
        }

      if (onCreate) {
        onCreate(newPhase.id);
      } else {
        onChange(newPhase.id);
      }
      
        setShowCreateForm(false);
      setIsOpen(false);
      toast.success('Phase created successfully');
    } catch (error) {
      console.error('Failed to create phase:', error);
      toast.error('Failed to create phase');
    } finally {
      setIsCreating(false);
    }
  };

  return (
      <div style={{ padding: DesignSystem.spacing.padding.lg }}>
        <div className="flex items-center justify-between mb-3">
          <h4 style={{
            fontSize: DesignSystem.typography.small.fontSize,
            fontFamily: DesignSystem.typography.small.fontFamily,
            fontWeight: 500,
            color: DesignSystem.colors.primary.text
          }}>
            New Phase
          </h4>
      <button
            onClick={handleCancelCreate}
            style={{
              padding: DesignSystem.spacing.padding.xs,
              color: DesignSystem.colors.primary.textMuted,
              transition: DesignSystem.animation.transition.normal
            }}
            className="hover:text-[#555]"
          >
            <FaTimes style={{ width: DesignSystem.sizes.icon.xs, height: DesignSystem.sizes.icon.xs }} />
          </button>
        </div>
        
        <div className="space-y-3">
          <input
            ref={localInputRef}
            type="text"
            value={localPhaseName}
            onChange={(e) => {
              e.stopPropagation();
              setLocalPhaseName(e.target.value);
            }}
            onKeyDown={handleLocalKeyPress}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            placeholder="Enter phase name..."
            className={cssClasses.textInput}
            style={{
              fontSize: DesignSystem.typography.small.fontSize,
              fontFamily: DesignSystem.typography.small.fontFamily,
            }}
            maxLength={50}
            autoComplete="off"
          />
          
          <div className="flex items-center justify-between">
            <span style={{
              fontSize: '11px',
              color: DesignSystem.colors.primary.textSecondary,
              fontFamily: DesignSystem.typography.small.fontFamily
            }}>
              {localPhaseName.length}/50 characters
        </span>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancelCreate}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  color: DesignSystem.colors.primary.textSecondary,
                  transition: DesignSystem.animation.transition.normal,
                  fontFamily: DesignSystem.typography.small.fontFamily
                }}
                className="hover:text-[#171717]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCreatePhaseWithName(localPhaseName)}
                disabled={!localPhaseName.trim() || isCreating}
                style={{
                  ...getPrimaryButtonStyle(),
                  fontSize: '11px',
                  padding: '6px 12px',
                  borderRadius: DesignSystem.borderRadius.lg
                }}
                className="flex items-center space-x-1.5"
              >
                {isCreating ? (
                  <>
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <FaCheck style={{ width: DesignSystem.sizes.icon.xs, height: DesignSystem.sizes.icon.xs }} />
                    <span>Create</span>
                  </>
                )}
      </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

    // Dropdown content component
  const DropdownContent = () => (
    <div 
      ref={dropdownRef}
      className={cssClasses.card}
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 99999,
        position: 'fixed',
        borderRadius: DesignSystem.borderRadius.xl,
        boxShadow: DesignSystem.shadows.lg
      }}
    >
      {/* Header */}
      <div style={{
        padding: `${DesignSystem.spacing.padding.md} ${DesignSystem.spacing.padding.lg}`,
        backgroundColor: DesignSystem.colors.background.gray50,
        borderBottom: `1px solid ${DesignSystem.colors.border.gray100}`,
        borderTopLeftRadius: DesignSystem.borderRadius.xl,
        borderTopRightRadius: DesignSystem.borderRadius.xl
      }}>
        <h3 style={{
          fontSize: DesignSystem.typography.small.fontSize,
          fontFamily: DesignSystem.typography.small.fontFamily,
          fontWeight: 500,
          color: DesignSystem.colors.primary.text,
          margin: 0
        }}>
          Select Phase
        </h3>
      </div>

      {/* Phase List */}
      <div>
        {phases.length > 0 ? (
          <div style={{ padding: `${DesignSystem.spacing.padding.sm} 0` }}>
            {phases.map((phase) => (
              <button
                key={phase.id}
                onClick={() => {
                  onChange(phase.id);
                  setIsOpen(false);
                  setShowCreateForm(false);
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: `10px ${DesignSystem.spacing.padding.lg}`,
                  fontSize: DesignSystem.typography.small.fontSize,
                  fontFamily: DesignSystem.typography.small.fontFamily,
                  transition: DesignSystem.animation.transition.normal,
                  backgroundColor: phase.id === selectedPhaseId ? DesignSystem.colors.link.light : 'transparent',
                  color: phase.id === selectedPhaseId ? DesignSystem.colors.link.primary : DesignSystem.colors.primary.textSecondary,
                  border: 'none',
                  cursor: 'pointer'
                }}
                className="hover:bg-[#fafafa] flex items-center justify-between group"
              >
                <span style={{ fontWeight: 500 }}>{formatPhaseNameForDisplay(phase.name)}</span>
                {phase.id === selectedPhaseId && (
                  <FaCheck style={{ 
                    width: DesignSystem.sizes.icon.xs, 
                    height: DesignSystem.sizes.icon.xs,
                    color: DesignSystem.colors.link.primary
                  }} />
                )}
              </button>
            ))}
          </div>
        ) : (
          <div style={{
            padding: `${DesignSystem.spacing.padding.xl} ${DesignSystem.spacing.padding.lg}`,
            textAlign: 'center',
            fontSize: DesignSystem.typography.small.fontSize,
            fontFamily: DesignSystem.typography.small.fontFamily,
            color: DesignSystem.colors.primary.textSecondary
          }}>
            No phases created yet
          </div>
        )}
      </div>

      {/* Create New Phase Section */}
      <div style={{
        borderTop: `1px solid ${DesignSystem.colors.border.gray100}`,
        backgroundColor: DesignSystem.colors.background.gray50,
        borderBottomLeftRadius: DesignSystem.borderRadius.xl,
        borderBottomRightRadius: DesignSystem.borderRadius.xl
      }}>
        {!showCreateForm ? (
                <button
            onClick={handleShowCreateForm}
            style={{
              width: '100%',
              padding: `${DesignSystem.spacing.padding.md} ${DesignSystem.spacing.padding.lg}`,
              textAlign: 'left',
              fontSize: DesignSystem.typography.small.fontSize,
              fontFamily: DesignSystem.typography.small.fontFamily,
              color: DesignSystem.colors.link.primary,
              transition: DesignSystem.animation.transition.normal,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer'
            }}
            className="flex items-center space-x-2"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = DesignSystem.colors.link.light;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FaPlus style={{ width: DesignSystem.sizes.icon.xs, height: DesignSystem.sizes.icon.xs }} />
            <span style={{ fontWeight: 500 }}>Create new phase</span>
          </button>
                  ) : (
          <CreatePhaseForm />
                  )}
              </div>
            </div>
  );

  return (
    <>
      {/* Badge Button */}
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: `6px ${DesignSystem.spacing.padding.md}`,
          borderRadius: DesignSystem.borderRadius.md,
          fontSize: DesignSystem.typography.tableHeader.fontSize,
          fontFamily: DesignSystem.typography.tableHeader.fontFamily,
          fontWeight: 500,
          backgroundColor: DesignSystem.colors.background.gray100,
          color: DesignSystem.colors.primary.textSecondary,
          border: 'none',
          cursor: 'pointer',
          transition: DesignSystem.animation.transition.normal,
          minWidth: 0
        }}
        className="hover:bg-[#eaeaea]"
      >
        <span style={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '60px'
        }}>
          {selectedPhase?.name ? formatPhaseNameForDisplay(selectedPhase.name) : 'Phase'}
        </span>
        <FaChevronDown 
          style={{ 
            marginLeft: '6px',
            width: '10px',
            height: '10px',
            transition: DesignSystem.animation.transition.normal,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }} 
        />
      </button>

      {/* Portal for dropdown to avoid z-index issues */}
      {isOpen && createPortal(
        <DropdownContent />,
        document.body
      )}
    </>
  );
}; 